import React, { useCallback, Fragment, useEffect, useRef } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { currency } from '@ecg-internal/aurora-js-formatters';
import {
  TextFieldMultiLine,
  Pills,
  DumbSelectionGroup,
  Checkbox,
  WarningInpage,
  TIcon,
} from '@hz-design-system/web-ui';

import { GA_TRACK_EVENTS_ACTIONS } from '../../../../../../utils/constants';
import { trackGAEvent } from '../../../../../../utils/gaTracking';
import isMobile from '../../../../../../utils/isMobile';
import Scheduler from './Scheduler';

import Classes from './MessageBox.module.scss';

import { TListingClient } from 'types/TListing';
import { TSellerPublicProfile } from 'types/TSellerPublicProfile';

export type TPill = {
  labelKey: string;
  contentKey: string;
  icon: TIcon;
  gaLabel: string;
};

type TMessageBoxProps = {
  onChange: (e?: any) => void;
  onCannedMessageChange?: (e?: any) => void;
  onTestDriveSchedulerChange?: (day: string, option: number) => void;
  message: string;
  cannedMessages?: string[];
  errorText?: string;
  initialPills?: TPill[];
  minRows?: number;
  bidInText?: number;
  withLowBidTip?: boolean;
  schedulerIsOpen?: boolean;
  selectedSchedulerOptions?: {
    [key: string]: number[];
  };
  schedulerOptions?: TSellerPublicProfile['testDriveSchedulerOptions'];
  priceInfo?: TListingClient['priceInfo'];
  warningBidMessage?: string;
} & Partial<typeof TextFieldMultiLine>;

// The message box should expand indefinitely
const MAX_ROWS_CLEAR = 999;
const DEFAULT_MESSAGE_MIN_ROWS = 4;

const MessageBox = ({
  onChange,
  onCannedMessageChange,
  onTestDriveSchedulerChange,
  message,
  cannedMessages = [],
  errorText,
  initialPills: pills,
  minRows = DEFAULT_MESSAGE_MIN_ROWS,
  bidInText,
  withLowBidTip,
  schedulerIsOpen,
  selectedSchedulerOptions,
  schedulerOptions,
  priceInfo,
  warningBidMessage,
  ...textFieldProps
}: TMessageBoxProps) => {
  const { t } = useI18nContext();
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const messageInput = useRef<HTMLInputElement | null>(null);

  const handleCheckboxClick = (clickedPill: TPill): void => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.AsqSelectedCan, clickedPill.gaLabel, { eventValue: 1 });
    if (onCannedMessageChange) {
      onCannedMessageChange(clickedPill);
    }
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  useEffect(() => {
    if (messageInput.current && 'focus' in messageInput.current && !isMobile()) {
      messageInput.current.focus();
      messageInput.current.selectionStart = messageInput.current.value.length;
      messageInput.current.selectionEnd = messageInput.current.value.length;
    }
  }, []);

  useEffect(() => {
    if (schedulerIsOpen && toggleRef?.current && 'scrollIntoView' in toggleRef.current) {
      toggleRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [schedulerIsOpen, toggleRef]);

  return (
    <div>
      <TextFieldMultiLine
        id="message"
        name="message"
        labelText={t('sellerContact.asq.modal.messageTitle')}
        value={message}
        errorText={errorText}
        onChange={handleChange}
        minRows={minRows}
        maxRows={MAX_ROWS_CLEAR}
        isRequired
        inputRef={messageInput}
        {...textFieldProps}
      />
      {withLowBidTip && bidInText && priceInfo?.priceCents && (
        <WarningInpage
          icon="alert"
          description={t('sellerContact.asq.modal.messageLowBidTip', {
            bidPrice: currency(bidInText, false, true),
            askingPrice: currency(priceInfo.priceCents, false, true),
          })}
          utilities={['marginTopXxxs']}
        />
      )}
      {warningBidMessage && <WarningInpage icon="alert" description={warningBidMessage} utilities={['marginTopXs']} />}
      {pills && pills.length > 0 && (
        <Pills className={Classes.checkboxWrapper}>
          <DumbSelectionGroup>
            {pills.map((option) => (
              <Fragment key={option.labelKey}>
                <Checkbox
                  className={Classes.cannedMessageInput}
                  onChange={() => handleCheckboxClick(option)}
                  isChecked={Boolean(cannedMessages?.includes(t(option.contentKey)))}
                  labelText={t(option.labelKey)}
                  icon={option.icon}
                  value={option.labelKey}
                />
                {schedulerIsOpen &&
                  schedulerOptions &&
                  onTestDriveSchedulerChange &&
                  selectedSchedulerOptions &&
                  option.gaLabel === 'c2b_test_drive' && (
                    <div ref={toggleRef} className={Classes.schedulerContainer}>
                      <Scheduler
                        schedulerOptions={schedulerOptions}
                        selectedSchedulerOptions={selectedSchedulerOptions}
                        onTestDriveSchedulerChange={onTestDriveSchedulerChange}
                      />
                    </div>
                  )}
              </Fragment>
            ))}
          </DumbSelectionGroup>
        </Pills>
      )}
    </div>
  );
};

export default MessageBox;
